<template>
  <div class="GptShareUserSearch" @click.stop>
    <div
      ref="inputContainer"
      class="GptShareUserSearch__input-container"
      @click="showDropdown"
    >
      <UITag
        v-for="user in visibleUsers"
        :key="getUserIdentity(user)"
        class="GptShareUserSearch__tag"
        hierarchy="secondary"
        theme="neutral"
        size="lg"
        isRemovable
        :title="user.email || user.name"
        @remove="$emit('removeUser', user)"
      >
        {{ capitalizeAllWords(user.name || user.email || '') }}
      </UITag>

      <!-- Temporary tags for pending email resolution. The remove control is
           withheld until the lookup settles, so the tag carries the spinner
           in its place. -->
      <UITag
        v-for="tempTag in temporaryTags"
        :key="tempTag.id"
        class="GptShareUserSearch__tag"
        hierarchy="secondary"
        theme="neutral"
        size="lg"
        :isRemovable="!tempTag.isLoading"
        :data-loading="tempTag.isLoading"
        :title="tempTag.email"
        @remove="removeTemporaryTag(tempTag.id)"
        @click.stop
      >
        {{ capitalizeAllWords(getTemporaryTagLabel(tempTag)) }}
        <UILoader
          v-if="tempTag.isLoading"
          class="GptShareUserSearch__tag-loader"
        />
      </UITag>

      <input
        v-model="search"
        data-testid="gpt-share-user-search"
        autocomplete="off"
        name="userSearch"
        class="GptShareUserSearch__input"
        :placeholder="!users?.length ? 'Names, emails, comma separated' : ''"
        @focus="showDropdown"
        @keydown="handleKeydown"
        @input="handleInput"
        @paste="handlePaste"
      />
    </div>

    <ul
      v-if="
        isDropdownVisible && (isLoadingSuggestions || userSuggestions.length)
      "
      ref="optionsContainer"
      class="GptShareUserSearch__options"
    >
      <div
        v-if="isLoadingSuggestions"
        class="GptShareUserSearch__options-loader-container"
      >
        <UILoader class="GptShareUserSearch__options-loader" />
      </div>

      <template v-else>
        <li
          v-for="suggestion in userSuggestions"
          :key="getUserIdentity(suggestion)"
          class="GptShareUserSearch__option"
          @click="addUser(suggestion)"
        >
          <div class="GptShareUserSearch__option-name">
            <span
              v-html="
                DOMPurify.sanitize(
                  highlightMatch(capitaliseName(suggestion.name)),
                )
              "
            ></span>
          </div>

          <div class="GptShareUserSearch__option-username-container">
            <div
              v-if="!suggestion.email"
              class="GptShareUserSearch__option-username"
            >
              <i>No email for this user</i>
            </div>
            <div
              v-else
              class="GptShareUserSearch__option-username"
              v-html="DOMPurify.sanitize(highlightMatch(suggestion.email))"
            />
          </div>
        </li>
      </template>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

import { UITag } from '@foundever/ui-lib'
import { debouncedWatch } from '@vueuse/core'
import DOMPurify from 'dompurify'
import { nanoid } from 'nanoid'

import { UserApi } from '@/apiClient/modules/user'
import type { IUserBulkSearchResponse } from '@/apiClient/modules/user'
import type { ISelectedUser } from '@/apiClient/types/user'
import { useToast } from '@/store/toast'
import { capitalizeAllWords } from '@/utils'
import { redactCardNumbers } from '@/utils/cardNumberRedaction'
import UILoader from '@/views/components/ui/loaders/UILoader.vue'

interface ITemporaryTag {
  id: string
  email: string
  isLoading: boolean
  user?: ISelectedUser
}

const emits = defineEmits(['addUser', 'removeUser'])
const props = withDefaults(
  defineProps<{
    users?: ISelectedUser[]
    usersWithAccess: ISelectedUser[]
  }>(),
  {
    users: () => [],
  },
)

const toastStore = useToast()

// Template refs
const inputContainer = ref<HTMLDivElement>()
const optionsContainer = ref<HTMLUListElement>()

const isLoadingSuggestions = ref(false)
const userSuggestions = ref<ISelectedUser[]>([])
const abortController = ref<AbortController>()
const temporaryTags = ref<ITemporaryTag[]>([])

type IUserIdentity = {
  id?: string | null
  employee_id?: string
  email?: string
  name?: string
}

const getUserIdentity = (user: IUserIdentity) =>
  user.id || user.employee_id || user.email || user.name

const visibleUsers = computed(() =>
  props.users.filter(
    (user) =>
      !temporaryTags.value.some(
        (tempTag) =>
          tempTag.user &&
          getUserIdentity(tempTag.user) === getUserIdentity(user),
      ),
  ),
)

const getTemporaryTagLabel = (tempTag: ITemporaryTag) =>
  tempTag.user?.name || tempTag.user?.email || tempTag.email

const syncDropdownWidth = async () => {
  await nextTick()
  if (inputContainer.value && optionsContainer.value) {
    const inputWidth = inputContainer.value.getBoundingClientRect().width
    optionsContainer.value.style.width = `${inputWidth}px`

    // Position the dropdown below the input
    const inputRect = inputContainer.value.getBoundingClientRect()
    optionsContainer.value.style.left = `${inputRect.left}px`
    optionsContainer.value.style.top = `${inputRect.bottom}px`
  }
}

const fetch = (query: string) => {
  if (abortController.value) {
    abortController.value.abort()
  }
  abortController.value = new AbortController()
  isLoadingSuggestions.value = true
  UserApi.search({ q: query }, { signal: abortController.value.signal })
    .then(({ items }) => {
      userSuggestions.value = items
        .slice(0, 10)
        .filter(
          (user) =>
            ![...props.usersWithAccess, ...props.users].some(
              (_user) => getUserIdentity(_user) === getUserIdentity(user),
            ),
        )
    })
    .finally(() => {
      isLoadingSuggestions.value = false
      abortController.value = undefined
    })
}

const search = ref('')
const isDropdownVisible = ref(false)

// Input validation function - accepts any non-empty text (email or name)
const isValidInput = (input: string): boolean => {
  return input.trim().length > 0
}

// Check if input already exists (in users, usersWithAccess, or temporary tags)
const isInputDuplicate = (input: string): boolean => {
  const normalizedInput = input.toLowerCase().trim()

  // Check in existing users (by email or name)
  const existsInUsers = [...props.users, ...props.usersWithAccess].some(
    (user) =>
      user.email.toLowerCase() === normalizedInput ||
      user.name.toLowerCase() === normalizedInput,
  )

  // Check in temporary tags
  const existsInTempTags = temporaryTags.value.some(
    (tag) => tag.email.toLowerCase() === normalizedInput,
  )

  return existsInUsers || existsInTempTags
}

// Handle paste events to process multiple comma-separated emails
const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault()
  // Card numbers are dropped (PCI) rather than becoming a person to look up.
  const pastedText = redactCardNumbers(
    event.clipboardData?.getData('text') || '',
    { shouldRemove: true },
  )

  if (pastedText.trim()) {
    processEmailList(pastedText)
  }
}

// Process a list of comma-separated inputs (emails or names)
const processEmailList = (inputList: string) => {
  const inputs = inputList
    .split(',')
    .map((input) => input.trim())
    .filter((input) => input.length > 0)

  const validInputs: string[] = []
  const invalidInputs: string[] = []
  const duplicateInputs: string[] = []

  // Validate each input and check for duplicates
  inputs.forEach((input) => {
    if (isValidInput(input)) {
      if (isInputDuplicate(input)) {
        duplicateInputs.push(input)
      } else {
        validInputs.push(input)
      }
    } else {
      invalidInputs.push(input)
    }
  })

  // Show error for invalid inputs
  if (invalidInputs.length > 0) {
    toastStore.onError(`Invalid input(s): ${invalidInputs.join(', ')}`)
  }

  // Show info for duplicate inputs
  if (duplicateInputs.length > 0) {
    toastStore.onInfo(
      `Duplicate input(s) skipped: ${duplicateInputs.join(', ')}`,
    )
  }

  // Create temporary tags for valid inputs
  if (validInputs.length > 0) {
    const tempTags: ITemporaryTag[] = validInputs.map((input) => ({
      id: nanoid(),
      email: input,
      isLoading: true,
    }))

    temporaryTags.value.push(...tempTags)

    // Clear the input
    search.value = ''

    if (tempTags.length > 1) {
      resolveUsersInBulk(tempTags)
    } else {
      resolveUser(tempTags[0])
    }
  }
}

// Handle input changes to detect comma-separated inputs
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  // Card numbers are dropped (PCI) before they can be searched for or become a tag.
  // Written back to the input too, so `v-model` reads the same value in either order.
  const value = redactCardNumbers(target.value, {
    inputElement: target,
    shouldRemove: true,
  })
  search.value = value

  // Check if the input ends with a comma
  if (value.endsWith(',')) {
    const input = value.slice(0, -1).trim()
    processSingleInput(input)
  }
}

// Handle keydown events
const handleKeydown = (event: KeyboardEvent) => {
  // Handle backspace/delete to remove last user tag
  if (event.key === 'Backspace' || event.key === 'Delete') {
    // Only remove last tag if input is empty
    if (!search.value.trim()) {
      // Check if there are temporary tags first
      if (temporaryTags.value.length > 0) {
        event.preventDefault()
        const lastTempTag = temporaryTags.value[temporaryTags.value.length - 1]
        removeTemporaryTag(lastTempTag.id)
        return
      }

      // If no temporary tags, check if there are regular users
      if (props.users.length > 0) {
        event.preventDefault()
        const lastUser = props.users[props.users.length - 1]
        emits('removeUser', lastUser)
        return
      }
    }
    return
  }

  // Allow normal input handling
  if (event.key !== ',' && event.key !== 'Enter') {
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    // If there's text in the input, treat it as if it ended with a comma
    if (search.value.trim()) {
      processSingleInput(search.value.trim())
    }
  }
}

// Process a single input (email or name)
const processSingleInput = (input: string) => {
  try {
    isDropdownVisible.value = false
    if (input && isValidInput(input)) {
      if (isInputDuplicate(input)) {
        // Duplicate input, show info and clear input
        toastStore.onInfo(`Duplicate input skipped: ${input}`)
        search.value = ''
      } else {
        // Create temporary tag
        const tempTag: ITemporaryTag = {
          id: nanoid(),
          email: input,
          isLoading: true,
        }
        temporaryTags.value.push(tempTag)

        // Clear the input
        search.value = ''

        // Resolve the input
        resolveUser(tempTag).catch((error) => {
          console.error(`Error processing single input ${input}:`, error)
        })
      }
    } else if (input) {
      // Invalid input, show error
      toastStore.onError(`Invalid input: ${input}`)
      search.value = ''
    }
  } catch (error) {
    console.error('Error in processSingleInput:', error)
    toastStore.onError(`Error processing input: ${input}`)
  }
}

const normalizeLookupValue = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .trim()

const findMatchingUser = (users: ISelectedUser[], query: string) => {
  const normalizedQuery = normalizeLookupValue(query)

  return users.find(
    (user) =>
      normalizeLookupValue(user.email) === normalizedQuery ||
      normalizeLookupValue(user.name) === normalizedQuery,
  )
}

const getUsersForInput = (response: IUserBulkSearchResponse, input: string) => {
  const normalizedInput = normalizeLookupValue(input)
  const allUsers = Object.values(response.results).flat()

  for (const [query, users] of Object.entries(response.results)) {
    if (normalizeLookupValue(query) === normalizedInput) {
      return users
    }
  }

  const matchingUser = findMatchingUser(allUsers, input)
  return matchingUser ? [matchingUser] : []
}

const resolveTemporaryTagResult = (
  tempTag: ITemporaryTag,
  user: ISelectedUser | undefined,
) => {
  const tagIndex = temporaryTags.value.findIndex((tag) => tag.id === tempTag.id)
  if (tagIndex === -1) return

  if (!user) {
    temporaryTags.value.splice(tagIndex, 1)
    toastStore.onError(`User not found: ${tempTag.email}`)
    return
  }

  temporaryTags.value.splice(tagIndex, 1, {
    ...tempTag,
    email: user.email || tempTag.email,
    isLoading: false,
    user,
  })

  try {
    emits('addUser', user)

    nextTick(() => {
      if (
        props.users.some(
          (selectedUser) =>
            getUserIdentity(selectedUser) === getUserIdentity(user),
        )
      ) {
        removeTemporaryTagById(tempTag.id)
      }
    })
  } catch (emitError) {
    console.error('Error emitting addUser event:', emitError)
    temporaryTags.value.splice(tagIndex, 1, tempTag)
    toastStore.onError(`Error adding user: ${tempTag.email}`)
  }
}

const resolveTemporaryTagFromResponse = (
  response: IUserBulkSearchResponse,
  tempTag: ITemporaryTag,
) => {
  try {
    const [user] = getUsersForInput(response, tempTag.email)
    resolveTemporaryTagResult(tempTag, user)
  } catch (error) {
    console.error(`Error processing bulk result for ${tempTag.email}:`, error)
    removeTemporaryTagById(tempTag.id)
    toastStore.onError(`Error processing user: ${tempTag.email}`)
  }
}

// Resolve a single submitted input with the regular user search endpoint.
const resolveUser = async (tempTag: ITemporaryTag) => {
  try {
    const { items: users } = await UserApi.search({ q: tempTag.email })
    resolveTemporaryTagResult(tempTag, users[0])
  } catch (error) {
    console.error(`Error searching for user ${tempTag.email}:`, error)
    removeTemporaryTagById(tempTag.id)
    toastStore.onError(`Error searching for user: ${tempTag.email}`)
  }
}

// Resolve submitted inputs with the bulk user search endpoint.
const resolveUsersInBulk = async (tempTags: ITemporaryTag[]) => {
  let response: IUserBulkSearchResponse

  try {
    response = await UserApi.bulkSearch({
      queries: tempTags.map((tempTag) => tempTag.email),
    })
  } catch (error) {
    console.error('Error in resolveUsersInBulk:', error)
    tempTags.forEach((tempTag) => removeTemporaryTagById(tempTag.id))
    toastStore.onError('Error searching for users')
    return
  }

  tempTags.forEach((tempTag) => {
    resolveTemporaryTagFromResponse(response, tempTag)
  })
}

const removeTemporaryTagById = (tagId: string) => {
  const tagIndex = temporaryTags.value.findIndex((tag) => tag.id === tagId)
  if (tagIndex !== -1) {
    temporaryTags.value.splice(tagIndex, 1)
  }
}

// Remove temporary tag
const removeTemporaryTag = (tagId: string) => {
  const tagIndex = temporaryTags.value.findIndex((tag) => tag.id === tagId)
  if (tagIndex !== -1) {
    const tempTag = temporaryTags.value[tagIndex]
    temporaryTags.value.splice(tagIndex, 1)
    if (tempTag.user) {
      emits('removeUser', tempTag.user)
    }
  }
}

const showDropdown = async () => {
  isDropdownVisible.value = true
  await syncDropdownWidth()
}

const hideDropdown = () => {
  isDropdownVisible.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.GptShareUserSearch')) {
    hideDropdown()
  }
}

const handleResize = () => {
  if (isDropdownVisible.value) {
    syncDropdownWidth()
  }
}

const capitaliseName = (name: string) =>
  name.split(' ').map(capitalizeAllWords).join(' ')

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', handleResize)
})

debouncedWatch(
  search,
  async (searchString) => {
    if (!searchString) return
    fetch(searchString)
    await showDropdown()
  },
  {
    debounce: 500,
  },
)

const addUser = (user: ISelectedUser) => {
  emits('addUser', user)
  search.value = ''
  isDropdownVisible.value = false
  userSuggestions.value = []
}

const highlightMatch = (text: string) => {
  const query = search.value
  if (!query) return text
  const regex = new RegExp(`(${query})`, 'gi')
  return text.replace(regex, '<strong>$1</strong>')
}
</script>

<style scoped lang="scss">
.GptShareUserSearch {
  position: relative;

  // Mirrors ui-lib's UIInputTextBase field chrome so this sits level with the
  // UIInputText fields it shares a form with. It stays hand-rolled because that
  // field is a fixed-height single line, while this box wraps chips and scrolls.
  .GptShareUserSearch__input-container {
    --border-color: var(--interactive-border-default);

    @include customScrollbar;
    display: flex;
    flex-wrap: wrap;
    gap: var(---fds-global-space-xxs);
    width: 100%;
    max-height: 6.25rem;
    padding: 0.375rem var(---fds-global-space-sm);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-s);
    background-color: var(--bg-primary);

    &:hover {
      --border-color: var(--interactive-border-hover);
    }

    // The container is a plain div, so the old `:focus` never matched and the
    // border stayed grey while typing; `:focus-within` is what ui-lib keys on.
    &:focus-within,
    &:active {
      --border-color: var(--interactive-border-active);
    }
  }

  .GptShareUserSearch__input-container--readonly {
    --border-color: var(--interactive-border-inactive);

    color: var(--text-tertiary);
    cursor: not-allowed;

    &:hover,
    &:active,
    &:focus-within {
      --border-color: var(--interactive-border-inactive);
    }

    input {
      pointer-events: none;
      cursor: not-allowed;
    }
  }

  .GptShareUserSearch__tag {
    // UITag has no pending state of its own; the lookup tags dim instead.
    &[data-loading='true'] {
      opacity: 0.7;
    }
  }

  // Sits inside UITag's label span, so it has to stay inline-level.
  .GptShareUserSearch__tag-loader {
    --size: 0.75rem;
    --color: var(--icon-tertiary);

    display: inline-block;
    vertical-align: middle;
    margin-left: var(---fds-global-space-xxxs);
  }

  .GptShareUserSearch__input {
    @include font-body-small-regular;
    flex-grow: 1;
    border: none;
    background-color: transparent;
    color: var(--text-primary);

    &:focus {
      outline: none;
    }

    // Was declared on the container, which has no placeholder to style.
    &::placeholder {
      color: var(--text-tertiary);
      opacity: 1;
    }
  }

  .GptShareUserSearch__options-loader-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: var(---fds-global-space-xxs) 0;
  }

  .GptShareUserSearch__options-loader {
    width: 1.25rem;
    height: 1.25rem;
  }

  // Matches the UIDropdown panel this menu sits alongside: same border, radius
  // and elevation token (the old literal shadow was already `--shadow-sm`).
  .GptShareUserSearch__options {
    @include customScrollbar;
    position: fixed;
    z-index: 2;
    max-height: 18.75rem;
    padding: var(---fds-global-space-xs);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background-color: var(--bg-primary);
    box-shadow: var(--shadow-sm);
    list-style: none;
  }

  .GptShareUserSearch__option {
    padding: var(---fds-global-space-xs) var(---fds-global-space-sm);
    border-radius: var(--radius-xs);
    cursor: pointer;

    &:hover {
      background-color: var(--interactive-bg-tertiary-hover);
    }
  }

  .GptShareUserSearch__option-name {
    @include font-body-small-regular;
    color: var(--text-primary);
  }

  .GptShareUserSearch__option-username {
    @include font-caption-regular;
    color: var(--text-secondary);
  }
}
</style>
